// Washi tape stickers for the Journal (Jade, 2026-10-26).
//
// A tape is a sticker of type "tape" with its own length (w) and thickness (h),
// stored like every other sticker as fractions of the editor's width. Its design
// is a small seamless tile (public/washi/<id>.webp, drawn in Canva) repeated
// along the strip, so making it longer never stretches the pattern; making it
// thicker scales the tile with it. The ends are torn by a clip-path that is
// worked out from the sticker's id, so a tape keeps the same tear every time.

export const WASHI_TAPES = [
  { id: "stripes",  label: "Pink stripes" },
  { id: "dots",     label: "Mint dots" },
  { id: "gingham",  label: "Blue gingham" },
  { id: "flowers",  label: "Tiny flowers" },
  { id: "hearts",   label: "Lavender hearts" },
  { id: "stars",    label: "Yellow stars" },
  { id: "waves",    label: "Peach waves" },
  { id: "pink",     label: "Plain pink" },
  { id: "confetti", label: "Confetti" },
];

export const TAPE_OPACITY = 0.9;

// Sizes in px on the editor (the sticker layer converts to fractions).
export const TAPE_DEFAULT_W = 180;
export const TAPE_DEFAULT_H = 24;
export const TAPE_MIN_W = 40;
export const TAPE_MAX_W = 1200;
export const TAPE_MIN_H = 14;
export const TAPE_MAX_H = 120;

export const clampTapeThickness = (h) => Math.max(TAPE_MIN_H, Math.min(TAPE_MAX_H, h));
export const clampTapeLength = (w) => Math.max(TAPE_MIN_W, Math.min(TAPE_MAX_W, w));

// Unknown ids (a design removed later) fall back to the first one, so an old
// note never shows an empty box.
export function tapeSrc(id) {
  const known = WASHI_TAPES.some(t => t.id === id) ? id : WASHI_TAPES[0].id;
  return `${process.env.PUBLIC_URL || ""}/washi/${known}.webp`;
}

function hashSeed(str) {
  let h = 2166136261;
  const s = String(str || "");
  for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 16777619); }
  return h >>> 0;
}
function rng(seed) {
  let a = seed || 1;
  return () => {
    a = (a + 0x6D2B79F5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

const TEAR_STEPS = 7;

// The torn ends as points: `left` and `right` are lists of { d, y } where d is
// how far the edge sits in from that end (px) and y is 0..1 down the strip.
// Depth follows the thickness, so a thin tape gets a fine tear.
export function tapeTear(seed, thickness) {
  const r = rng(hashSeed(seed));
  const depth = Math.max(2, Math.min(8, thickness * 0.16));
  const edge = () => Array.from({ length: TEAR_STEPS + 1 }, (_, i) => ({ d: r() * depth, y: i / TEAR_STEPS }));
  return { left: edge(), right: edge() };
}

const n = (v) => Math.round(v * 100) / 100;

// CSS clip-path for the tear. The right end is measured from the right edge with
// calc(), so it stays torn while the length is being dragged.
export function tapeClipPath(seed, thickness) {
  const { left, right } = tapeTear(seed, thickness);
  const pts = [
    ...left.map(p => `${n(p.d)}px ${n(p.y * 100)}%`),
    ...right.slice().reverse().map(p => `calc(100% - ${n(p.d)}px) ${n(p.y * 100)}%`),
  ];
  return `polygon(${pts.join(", ")})`;
}

// The same outline in px for a canvas of w x h (used by Save as Image, since
// html2canvas ignores clip-path).
export function tapeOutline(seed, w, h) {
  const { left, right } = tapeTear(seed, h);
  return [
    ...left.map(p => [p.d, p.y * h]),
    ...right.slice().reverse().map(p => [w - p.d, p.y * h]),
  ];
}

// Paints a tape onto a canvas: the tile repeated along the strip at the tape's
// thickness, cut to the torn outline. `img` must already be loaded.
export function drawTape(ctx, img, seed, w, h, scale = 1) {
  const tileW = img.naturalWidth * (h / img.naturalHeight);
  ctx.save();
  ctx.scale(scale, scale);
  ctx.beginPath();
  tapeOutline(seed, w, h).forEach(([x, y], i) => (i ? ctx.lineTo(x, y) : ctx.moveTo(x, y)));
  ctx.closePath();
  ctx.clip();
  ctx.globalAlpha = TAPE_OPACITY;
  // A hair of overlap so no hairline shows where two tiles meet.
  for (let x = 0; x < w; x += tileW) ctx.drawImage(img, x, 0, tileW + 0.6, h);
  ctx.restore();
}
